import {
  EDIT_IMPLIED_ACTIONS,
  PERMISSION_ACTIONS,
  toCodename,
} from "./constants";

/**
 * Normalize `/me/permissions/` payload into a codename → boolean map.
 * Prefers `permissions` object (`true` / `false`). Falls back to `codenames[]`.
 *
 * @param {unknown} payload
 * @returns {Record<string, boolean>}
 */
export function normalizePermissions(payload) {
  if (!payload || typeof payload !== "object") {
    return {};
  }

  // New API shape: { permissions: { "users.view": true, ... }, codenames: [...] }
  if (
    payload.permissions &&
    typeof payload.permissions === "object" &&
    !Array.isArray(payload.permissions)
  ) {
    const map = {};
    Object.entries(payload.permissions).forEach(([codename, granted]) => {
      if (typeof codename === "string" && codename.trim()) {
        map[codename.trim()] = granted === true;
      }
    });
    return map;
  }

  // Legacy / alternate: { codenames: string[] } or bare string[]
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload.codenames)
    ? payload.codenames
    : [];

  return list.reduce((map, code) => {
    if (typeof code === "string" && code.trim()) {
      map[code.trim()] = true;
    }
    return map;
  }, {});
}

/**
 * @deprecated Use normalizePermissions. Kept for existing imports.
 */
export function normalizePermissionCodenames(payload) {
  return new Set(
    Object.entries(normalizePermissions(payload))
      .filter(([, granted]) => granted === true)
      .map(([codename]) => codename)
  );
}

/**
 * @param {Record<string, boolean> | Set<string> | string[] | null | undefined} permissions
 * @param {string} codename
 */
export function hasPermission(permissions, codename) {
  if (!codename) return true;
  if (!permissions) return false;

  // New shape: boolean map
  if (
    typeof permissions === "object" &&
    !Array.isArray(permissions) &&
    !(permissions instanceof Set)
  ) {
    return permissions[codename] === true;
  }

  // Legacy Set / array of granted codenames
  if (permissions instanceof Set) {
    return permissions.has(codename);
  }

  if (Array.isArray(permissions)) {
    return permissions.includes(codename);
  }

  return false;
}

/**
 * Resolve an action to the API action used for the check.
 * `delete` / `deactivate` map to `edit`.
 * @param {string} action
 */
export function resolveAction(action) {
  if (EDIT_IMPLIED_ACTIONS.has(action)) {
    return PERMISSION_ACTIONS.EDIT;
  }
  return action;
}

/**
 * Check module + action against a permission map.
 * @param {Record<string, boolean> | Set<string> | string[] | null | undefined} permissions
 * @param {string} module
 * @param {string} action
 */
export function can(permissions, module, action) {
  if (!module || !action) return true;
  const resolved = resolveAction(action);
  return hasPermission(permissions, toCodename(module, resolved));
}

/**
 * True if the user can view the module (any of view / view_own).
 * Useful for menu visibility and list pages.
 */
export function canViewModule(permissions, module) {
  return (
    can(permissions, module, PERMISSION_ACTIONS.VIEW) ||
    can(permissions, module, PERMISSION_ACTIONS.VIEW_OWN)
  );
}

/**
 * Evaluate a single requirement node.
 * Supports:
 * - string codename: "users.view"
 * - { module, action }
 * - { any: Requirement[] } / { all: Requirement[] } (nested groups)
 *
 * @param {Record<string, boolean> | Set<string> | string[] | null | undefined} permissions
 * @param {unknown} req
 */
function evaluateRequirement(permissions, req) {
  if (req == null) return true;

  if (typeof req === "string") {
    return hasPermission(permissions, req);
  }

  if (typeof req !== "object") return false;

  if (Array.isArray(req.any)) {
    return checkRequirements(permissions, req.any, "any");
  }

  if (Array.isArray(req.all)) {
    return checkRequirements(permissions, req.all, "all");
  }

  if (req.module && req.action) {
    return can(permissions, req.module, req.action);
  }

  return false;
}

/**
 * @param {Record<string, boolean> | Set<string> | string[] | null | undefined} permissions
 * @param {unknown} requirements
 * @param {'any' | 'all'} [mode='any']
 */
export function checkRequirements(permissions, requirements, mode = "any") {
  if (!requirements || (Array.isArray(requirements) && requirements.length === 0)) {
    return true;
  }

  // Top-level nested group: { any: [...] } or { all: [...] }
  if (
    !Array.isArray(requirements) &&
    typeof requirements === "object" &&
    (Array.isArray(requirements.any) || Array.isArray(requirements.all))
  ) {
    return evaluateRequirement(permissions, requirements);
  }

  const list = Array.isArray(requirements) ? requirements : [requirements];
  const results = list.map((req) => evaluateRequirement(permissions, req));

  return mode === "all" ? results.every(Boolean) : results.some(Boolean);
}
